import {
  BusinessFileType,
  IBusinessFile,
} from "../../../../interfaces/businessFile.interface";
import { downloadFile } from "../../../../utils/downloadFile";

// Typed as a full Record so adding a BusinessFileType without a label is a compile error.
const FILE_TYPE_LABELS: Record<BusinessFileType, string> = {
  [BusinessFileType.TMA_FILE]: "TMA Contract",
  [BusinessFileType.TMA_METADATA_FILE]: "TMA Signature",
  [BusinessFileType.COMPANY_INFO_FILE]: "Company Info Extract",
  [BusinessFileType.BANK_CONFIRMATION_FILE]: "Bank Account Confirmation",
  [BusinessFileType.UNDERLYING_COMPANY_DOCUMENT_FILE]:
    "Underlying Company Document",
  [BusinessFileType.UBO_DOCUMENT_FILE]: "UBO Document",
  [BusinessFileType.IDV_DOCUMENT_FILE]: "Identity Verification Document",
  [BusinessFileType.COMPRESSED_ATTACHMENTS]: "Compressed Attachments",
  [BusinessFileType.PERSON_IDENTITY_FRONT_FILE]:
    "Identity Document (front side)",
  [BusinessFileType.PERSON_IDENTITY_BACK_FILE]: "Identity Document (back side)",
};

export function getFileDisplayName(fileType?: BusinessFileType): string {
  if (!fileType) return "";
  return FILE_TYPE_LABELS[fileType] ?? humanizeFileType(fileType);
}

// A type the backend added ahead of this client still gets a readable tag.
function humanizeFileType(fileType: string): string {
  return fileType
    .replace(/_FILE$/, "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

type OpenableBusinessFile = Pick<
  IBusinessFile,
  "downloadUrl" | "file" | "fileName" | "fileExtension"
>;

export function canOpenBusinessFile(file: OpenableBusinessFile): boolean {
  return Boolean(file.downloadUrl || file.file);
}

// Inline `file` is the fallback for a backend that still returns bytes.
export function openBusinessFile(file: OpenableBusinessFile): void {
  if (file.downloadUrl) {
    window.open(file.downloadUrl, "_blank", "noopener,noreferrer");
    return;
  }
  if (file.file) downloadFile(file.file, file.fileName, file.fileExtension);
}

/**
 * Delay until the list must be re-signed: the shortest `expiresInSeconds` among
 * rows we signed, less `bufferMs`. Undefined when no row carries a TTL.
 */
export function nextBusinessFilesRefreshMs(
  files: Pick<IBusinessFile, "downloadUrl" | "expiresInSeconds">[],
  bufferMs: number
): number | undefined {
  const ttls = files
    .filter((file) => file.downloadUrl && (file.expiresInSeconds ?? 0) > 0)
    .map((file) => file.expiresInSeconds as number);
  if (!ttls.length) return undefined;
  return Math.max(0, Math.min(...ttls) * 1000 - bufferMs);
}
